import axios from 'axios';
import { api } from '../../lib/api.js';

/// O DRE e o Fluxo de caixa chegam prontos da API (ADR 0062): a tela só mostra, abre e esconde.

export type ReportDrill =
  | { kind: 'ACCOUNTS'; accountIds: string[] }
  | { kind: 'NONE' }
  | { kind: 'WAITING' }
  | { kind: 'OFF_SOURCE' }
  | { kind: 'ADJUSTMENTS'; originMonth: string };

/// O mês diante do fechamento contábil (ADR 0064): fechado em todas as unidades da leitura, em
/// parte delas ou em nenhuma, e se algum mês fechado mudou depois de fechado.
export interface ReportMonthClosing {
  state: 'CLOSED' | 'PARTIAL' | 'OPEN';
  closedUnits: number;
  totalUnits: number;
  changed: boolean;
}

export interface DreRow {
  key: string;
  kind: 'GROUP' | 'ACCOUNT' | 'SUBTOTAL' | 'RESULT' | 'WARNING' | 'INFO' | 'PERCENT' | 'SECTION';
  label: string;
  code: string | null;
  level: number;
  parentKey: string | null;
  hasChildren: boolean;
  values: Array<number | null>;
  total: number | null;
  drill: ReportDrill | null;
}

export interface DreReport {
  year: number;
  months: string[];
  currentMonth: string | null;
  closing: ReportMonthClosing[];
  rows: DreRow[];
  summary: {
    netRevenueCents: number;
    resultCents: number;
    marginRatio: number | null;
    noAccountCents: number;
    lastClassifiedRatio: number | null;
  };
}

export type CashDirection = 'INFLOW' | 'OUTFLOW';

export type CashDrill =
  | { kind: 'ACCOUNTS'; accountIds: string[]; direction: CashDirection }
  | { kind: 'NONE'; direction: CashDirection }
  | { kind: 'OVERDUE'; direction: CashDirection; dueMonth: string | null };

export interface CashCell {
  realized: number;
  planned: number;
}

export interface CashRow {
  key: string;
  kind: 'BALANCE' | 'SECTION' | 'GROUP' | 'ACCOUNT' | 'WARNING' | 'SUBTOTAL' | 'RESULT';
  label: string;
  code: string | null;
  level: number;
  parentKey: string | null;
  hasChildren: boolean;
  outsideDre: boolean;
  cells: CashCell[];
  total: CashCell;
  drill: CashDrill | null;
}

export interface CashFlowReport {
  year: number;
  months: string[];
  currentMonth: string | null;
  closing: ReportMonthClosing[];
  rows: CashRow[];
  summary: {
    balanceNowCents: number;
    balanceEndCents: number;
    lowest: { month: string; cents: number } | null;
    noAccountCents: number;
    variableRecurrences: number;
  };
}

export interface ReportFact {
  date: string;
  description: string;
  detail: string;
  origin:
    | 'OBLIGATION'
    | 'DECLARED'
    | 'DIFFERENCE'
    | 'WAITING'
    | 'SETTLEMENT'
    | 'RECURRENCE'
    | 'TRANSFER'
    | 'COUNTED_ONCE'
    | 'ADJUSTMENT';
  status: 'PAID' | 'OPEN' | 'OVERDUE' | 'NOT_GENERATED' | null;
  unitName: string;
  account: { code: string; name: string } | null;
  cents: number;
}

export interface ReportFactPage {
  rows: ReportFact[];
  total: number;
  totalCents: number;
}

export interface FinanceAccountOption {
  id: string;
  name: string;
  unitName: string;
  active: boolean;
}

function dreLine(drill: ReportDrill) {
  if (drill.kind === 'ACCOUNTS')
    return { line: 'accounts', accountIds: drill.accountIds.join(',') };
  if (drill.kind === 'NONE') return { line: 'none' };
  if (drill.kind === 'WAITING') return { line: 'waiting' };
  if (drill.kind === 'ADJUSTMENTS') return { line: 'adjustments', originMonth: drill.originMonth };
  return { line: 'off-source' };
}

function cashLine(drill: CashDrill) {
  if (drill.kind === 'ACCOUNTS')
    return { line: 'accounts', accountIds: drill.accountIds.join(',') };
  if (drill.kind === 'NONE') return { line: 'none' };
  return { line: 'overdue', ...(drill.dueMonth ? { dueMonth: drill.dueMonth } : {}) };
}

export type ExportFormat = 'xlsx' | 'pdf';

export interface ExportedReport {
  blob: Blob;
  fileName: string;
}

function textOf(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsText(blob);
  });
}

/// O arquivo vem como Blob, e a recusa também: sem ler o corpo, a pessoa veria só a frase
/// genérica em vez do motivo (moeda estrangeira, por exemplo).
async function download(
  url: string,
  params: Record<string, string | number>,
  fileName: string,
): Promise<ExportedReport> {
  try {
    const response = await api.get<Blob>(url, { params, responseType: 'blob' });
    return { blob: response.data, fileName };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
      try {
        error.response.data = JSON.parse(await textOf(error.response.data)) as unknown;
      } catch {
        // Corpo que não é JSON: fica a frase genérica.
      }
    }
    throw error;
  }
}

export const reportsApi = {
  exportDre: (input: {
    month: string;
    today: string;
    format: ExportFormat;
    percent: boolean;
    facts: boolean;
  }) =>
    download(
      '/tenant/accounting/reports/dre/export',
      {
        month: input.month,
        today: input.today,
        format: input.format,
        show: input.percent ? 'percent' : 'values',
        facts: String(input.facts),
      },
      `dre-${input.month.slice(0, 4)}.${input.format}`,
    ),
  exportCashFlow: (input: {
    year: number;
    today: string;
    format: ExportFormat;
    planned: boolean;
    facts: boolean;
    financeAccountId: string | null;
  }) =>
    download(
      '/tenant/accounting/reports/cash-flow/export',
      {
        year: input.year,
        today: input.today,
        format: input.format,
        planned: String(input.planned),
        facts: String(input.facts),
        ...(input.financeAccountId ? { financeAccountId: input.financeAccountId } : {}),
      },
      `fluxo-de-caixa-${input.year}.${input.format}`,
    ),
  dre: async (month: string, today: string) =>
    (await api.get<DreReport>('/tenant/accounting/reports/dre', { params: { month, today } })).data,
  dreFacts: async (month: string, drill: ReportDrill) =>
    (
      await api.get<ReportFactPage>('/tenant/accounting/reports/dre/facts', {
        params: { month, ...dreLine(drill) },
      })
    ).data,
  cashFlow: async (year: number, today: string, financeAccountId: string | null) =>
    (
      await api.get<CashFlowReport>('/tenant/accounting/reports/cash-flow', {
        params: { year, today, ...(financeAccountId ? { financeAccountId } : {}) },
      })
    ).data,
  cashFacts: async (input: {
    month: string;
    drill: CashDrill;
    today: string;
    planned: boolean;
    financeAccountId: string | null;
  }) =>
    (
      await api.get<ReportFactPage>('/tenant/accounting/reports/cash-flow/facts', {
        params: {
          month: input.month,
          direction: input.drill.direction,
          today: input.today,
          planned: String(input.planned),
          ...cashLine(input.drill),
          ...(input.financeAccountId ? { financeAccountId: input.financeAccountId } : {}),
        },
      })
    ).data,
  financeAccounts: async () =>
    (
      await api.get<{ accounts: FinanceAccountOption[] }>(
        '/tenant/accounting/reports/finance-accounts',
      )
    ).data.accounts,
};
